import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../../config/supabase';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { Info } from '../../lib/lucide-proxy';

const DigitalMenu = () => {
  const { slug } = useParams();
  const [brewery, setBrewery] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: breweryData, error: breweryError } = await supabase
          .from('breweries')
          .select('*')
          .eq('slug', slug)
          .single();
        
        if (breweryError) throw breweryError;
        setBrewery(breweryData);

        const { data: productsData } = await supabase
          .from('products')
          .select('*, category:categories(name)')
          .eq('brewery_id', breweryData.id)
          .eq('is_available', true)
          .order('category_id');
          
        setProducts(productsData || []);
      } catch (error) {
        console.error('Error fetching menu:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>;
  if (!brewery) return <div className="min-h-screen flex items-center justify-center">Menu não encontrado</div>;

  // Group products by category
  const groupedProducts = products.reduce((acc, product) => {
    const cat = product.category?.name || 'Outros';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(product);
    return acc;
  }, {});

  return (
    <>
      <Helmet>
        <title>Menu - {brewery.name}</title>
      </Helmet>
      
      <div className="min-h-screen bg-white pb-12 max-w-md mx-auto shadow-2xl">
        {/* Header */}
        <div className="relative h-48">
          <img src={brewery.cover_image} alt={brewery.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/40" />
          <div className="absolute bottom-0 left-0 p-6 text-white">
            <h1 className="font-bold text-2xl mb-1">{brewery.name}</h1>
            <p className="text-sm opacity-90">{brewery.address}</p>
          </div>
        </div>

        <div className="p-6 space-y-8">
          {/* Info Card */}
          <div className="bg-primary-50 p-4 rounded-xl border border-primary-100 flex items-start">
            <Info className="w-5 h-5 text-primary-600 mr-3 flex-shrink-0" />
            <p className="text-sm text-primary-800">
              Este é um cardápio digital para visualização. Para fazer pedidos, acesse nossa 
              <a href={`/loja/${brewery.slug}`} className="font-bold underline ml-1">Loja Online</a>.
            </p>
          </div>

          {/* Menu Categories */}
          {Object.entries(groupedProducts).map(([category, items]) => (
            <div key={category}>
              <h2 className="font-display font-bold text-xl text-dark-900 mb-4 border-b border-gray-100 pb-2">
                {category}
              </h2>
              <div className="space-y-4">
                {items.map(product => (
                  <div key={product.id} className="flex gap-4">
                    <div className="flex-1">
                      <div className="flex justify-between items-start mb-1">
                        <h3 className="font-bold text-dark-900">{product.name}</h3>
                        <span className="font-semibold text-primary-600">
                          R$ {product.price.toFixed(2)}
                        </span>
                      </div>
                      <p className="text-sm text-dark-500 line-clamp-2 mb-2">{product.description}</p>
                      <div className="flex gap-2">
                        {product.abv && <span className="text-xs bg-gray-100 px-2 py-0.5 rounded text-dark-600">ABV {product.abv}%</span>}
                        {product.ibu && <span className="text-xs bg-gray-100 px-2 py-0.5 rounded text-dark-600">IBU {product.ibu}</span>}
                      </div>
                    </div>
                    {product.image && (
                      <img src={product.image} alt={product.name} className="w-20 h-20 rounded-lg object-cover bg-gray-100" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default DigitalMenu;